import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import LinkOff from "@mui/icons-material/LinkOff";
import SendOutlined from "@mui/icons-material/SendOutlined";
import ticketService from "../../../services/ticketService";
import { featureFlags, ticketMessagePollingIntervalSec } from "../../../config/featureConfig";
import { useTicketUnread } from "../../../context/ticketUnreadContext";
import { MAX_MESSAGE_LEN } from "../../../services/schemas/ticketSchemas";
import { validateFileByMimeAndSignature } from "../../../services/fileValidation";
import {
  applyPollResult,
  buildDescriptionMessage,
  buildSentChatMessage,
  collectOwnUnreadMessageIds,
  formatTicketDate,
  getPollAfterTimestamp,
  isChatWritable,
  mergeChatMessages,
  sortChatMessagesAscending,
} from "../../../services/ticketUtils";
import { dispatchAppError } from "../../../services/errors/errorDispatcher";
import { ValidationError } from "../../../services/errors/ValidationError";
import TicketChatMessage from "./TicketChatMessage";
import TicketChatSkeleton from "./TicketChatSkeleton";
import {
  chatDividerSx,
  chatInputSx,
  chatPaginationChipSx,
  chatThreadSx,
  roundActionIconSx,
} from "./ticketChatConstants";

const CHAT_DISABLED_TOOLTIP = "Finché il ticket non passa in lavorazione la chat è disattivata";

function downloadBase64File(base64, fileName) {
  const link = document.createElement("a");
  link.href = `data:application/octet-stream;base64,${base64}`;
  link.download = fileName || "allegato";
  link.click();
}

const TicketChat = ({ ticket, disabled, onMessagesRead }) => {
  const { refreshSummary } = useTicketUnread();
  const threadRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesRef = useRef([]);
  const onMessagesReadRef = useRef(onMessagesRead);
  const scrollToBottomRef = useRef(false);

  useEffect(() => {
    onMessagesReadRef.current = onMessagesRead;
  });

  const [messages, setMessages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingAttachmentName, setEditingAttachmentName] = useState(null);
  const [attachmentRemoved, setAttachmentRemoved] = useState(false);

  messagesRef.current = messages;

  const chatEnabled = isChatWritable(ticket.state) && !disabled;
  const isEditing = Boolean(editingId);
  const hasExistingAttachment = Boolean(editingAttachmentName && !attachmentRemoved);
  const hasPendingAttachment = Boolean(file) || hasExistingAttachment;
  const canSubmit = Boolean(messageText.trim()) || hasPendingAttachment;

  const resetComposer = useCallback(() => {
    setEditingId(null);
    setEditingAttachmentName(null);
    setAttachmentRemoved(false);
    setMessageText("");
    setFile(null);
    setAttachmentError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const descriptionMessage = useMemo(
    () => buildDescriptionMessage(ticket.id, ticket.createdAt, ticket.description),
    [ticket.id, ticket.createdAt, ticket.description]
  );

  const visibleMessages = useMemo(
    () => sortChatMessagesAscending(messages).filter((msg) => msg.id !== editingId),
    [messages, editingId]
  );
  const hasChatMessages = visibleMessages.length > 0;
  const showPaginationChip = totalCount > 10;

  const loadPage = useCallback(async (pageToLoad, { append = false, preserveScroll = false } = {}) => {
    const setLoader = append ? setLoadingMore : setLoading;
    const threadEl = preserveScroll ? threadRef.current : null;
    const prevScrollHeight = threadEl?.scrollHeight ?? 0;
    const prevScrollTop = threadEl?.scrollTop ?? 0;

    setLoader(true);
    try {
      const res = await ticketService.listMessages({ ticketId: ticket.id, page: pageToLoad });
      setTotalCount(res.totalCount);
      setPage(res.page);
      setMessages((prev) => (append ? mergeChatMessages(prev, res.items) : res.items));
      if (!append && pageToLoad === 0) {
        scrollToBottomRef.current = true;
        onMessagesReadRef.current?.();
      } else if (append && threadEl) {
        requestAnimationFrame(() => {
          const el = threadRef.current;
          if (!el) return;
          el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight);
        });
      }
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        dispatchAppError(e?.response?.data?.message ?? e?.message ?? "Errore caricamento messaggi");
      }
    } finally {
      setLoader(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    setMessages([]);
    setPage(0);
    setTotalCount(0);
    resetComposer();
    loadPage(0, { append: false });
  }, [ticket.id, loadPage, resetComposer]);

  const pollNewMessages = useCallback(async () => {
    if (!featureFlags.enableTicketPolling) return;
    try {
      const currentMessages = messagesRef.current;
      const res = await ticketService.pollMessages(
        {
          ticketId: ticket.id,
          afterTimestamp: getPollAfterTimestamp(currentMessages),
          ownMessageIds: collectOwnUnreadMessageIds(currentMessages, (msg) => !msg.operator),
        },
        { silent: true }
      );
      const hadNewItems = Boolean(res.items?.length);
      const hadReadUpdates = Boolean(res.readMessageIds?.length);
      if (!hadNewItems && !hadReadUpdates) return;

      setMessages((prev) => {
        const next = applyPollResult(prev, res);
        const added = next.length - prev.length;
        if (added > 0) {
          setTotalCount((count) => count + added);
          scrollToBottomRef.current = true;
        }
        return next;
      });
      if (hadNewItems || hadReadUpdates) {
        onMessagesReadRef.current?.();
        refreshSummary();
      }
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        console.warn("ticket message poll failed", e);
      }
    }
  }, [refreshSummary, ticket.id]);

  useEffect(() => {
    if (!featureFlags.enableTicketPolling || loading) return undefined;
    pollNewMessages();
    const ms = Math.max(1, ticketMessagePollingIntervalSec) * 1000;
    const timer = setInterval(pollNewMessages, ms);
    return () => clearInterval(timer);
  }, [ticket.id, pollNewMessages, loading]);

  useLayoutEffect(() => {
    if (!scrollToBottomRef.current) return;
    scrollToBottomRef.current = false;
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [visibleMessages, editingId]);

  const canLoadMore = messages.length < totalCount;
  const loadMoreLabel = loadingMore
    ? "Caricamento..."
    : (canLoadMore ? "Carica altro" : "Non ci sono altri messaggi");

  const handleFileChange = useCallback(async (event) => {
    if (!chatEnabled) return;
    const selected = event.target.files?.[0];
    if (!selected) return;
    event.target.value = "";
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (selected.size > 2 * 1024 * 1024) {
      setFile(null);
      setAttachmentError("Allegato non valido (max 2MB, consentiti: JPEG/PNG/PDF)");
      return;
    }
    const mimeValidation = await validateFileByMimeAndSignature(selected, allowedTypes);
    if (!mimeValidation.ok) {
      setFile(null);
      setAttachmentError(mimeValidation.message);
      return;
    }
    setAttachmentError("");
    setFile(selected);
    setAttachmentRemoved(false);
  }, [chatEnabled]);

  const handleRemoveAttachment = useCallback(() => {
    setFile(null);
    setAttachmentError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (editingAttachmentName) {
      setAttachmentRemoved(true);
    }
  }, [editingAttachmentName]);

  const handleSend = useCallback(async () => {
    if (!chatEnabled || !canSubmit) return;
    const trimmed = messageText.trim();
    setSending(true);
    try {
      if (isEditing) {
        await ticketService.editMessage({
          ticketId: ticket.id,
          messageId: editingId,
          action: "edit",
          message: trimmed || undefined,
          file,
          removeAttachment: Boolean(editingAttachmentName && attachmentRemoved && !file),
          existingAttachmentKept: hasExistingAttachment,
        });
        const nextAttachmentName = file
          ? file.name
          : (attachmentRemoved ? null : editingAttachmentName);
        setMessages((prev) => prev.map((msg) => (
          msg.id === editingId
            ? { ...msg, message: trimmed || null, attachmentName: nextAttachmentName }
            : msg
        )));
        resetComposer();
        return;
      }

      const res = await ticketService.sendMessage({
        ticketId: ticket.id,
        message: trimmed || undefined,
        file,
      });
      const sentMessage = buildSentChatMessage({
        messageId: res.messageId,
        message: trimmed,
        attachmentName: file?.name ?? null,
      });
      setMessages((prev) => mergeChatMessages(prev, [sentMessage]));
      setTotalCount((prev) => prev + 1);
      scrollToBottomRef.current = true;
      resetComposer();
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        dispatchAppError(e?.response?.data?.message ?? e?.message ?? "Errore invio messaggio");
      }
    } finally {
      setSending(false);
    }
  }, [
    attachmentRemoved,
    canSubmit,
    chatEnabled,
    editingAttachmentName,
    editingId,
    file,
    hasExistingAttachment,
    isEditing,
    messageText,
    resetComposer,
    ticket.id,
  ]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
    if (event.key === "Escape" && isEditing) {
      event.preventDefault();
      resetComposer();
    }
  }, [handleSend, isEditing, resetComposer]);

  const handleDownload = useCallback(async (messageId) => {
    try {
      const res = await ticketService.downloadAttachment({ ticketId: ticket.id, messageId });
      downloadBase64File(res.attachmentBase64, res.attachmentName);
    } catch (e) {
      dispatchAppError(e?.response?.data?.message ?? e?.message ?? "Errore download allegato");
    }
  }, [ticket.id]);

  const handleDelete = useCallback(async (messageId) => {
    if (editingId === messageId) {
      resetComposer();
    }
    try {
      await ticketService.editMessage({
        ticketId: ticket.id,
        messageId,
        action: "delete",
      });
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      setTotalCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      dispatchAppError(e?.response?.data?.message ?? e?.message ?? "Errore eliminazione messaggio");
    }
  }, [editingId, resetComposer, ticket.id]);

  const handleEditStart = useCallback((msg) => {
    setEditingId(msg.id);
    setMessageText(msg.message ?? "");
    setEditingAttachmentName(msg.attachmentName ?? null);
    setAttachmentRemoved(false);
    setFile(null);
    setAttachmentError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const renderMessage = (msg) => {
    const isOwn = !msg.operator;
    return (
      <TicketChatMessage
        key={msg.id}
        msg={msg}
        isOwn={isOwn}
        onEditStart={handleEditStart}
        onDelete={handleDelete}
        onDownload={handleDownload}
        showEditActions={Boolean(msg.editable && chatEnabled && !msg.isDescription && !isEditing)}
        showReadReceipt={isOwn && !msg.isDescription}
        formatDate={formatTicketDate}
      />
    );
  };

  const pendingAttachmentLabel = file?.name ?? (hasExistingAttachment ? editingAttachmentName : null);
  const showAttachmentRemove = Boolean(file || hasExistingAttachment);

  const messageField = (
    <TextField
      multiline
      minRows={1}
      maxRows={4}
      fullWidth
      size="small"
      placeholder={isEditing ? "Modifica messaggio" : "Scrivi un messaggio"}
      value={messageText}
      onChange={(e) => setMessageText(e.target.value)}
      onKeyDown={handleKeyDown}
      disabled={!chatEnabled || sending}
      inputProps={{ maxLength: MAX_MESSAGE_LEN }}
      sx={chatInputSx}
    />
  );

  return (
    <Box>
      <Box ref={threadRef} sx={chatThreadSx}>
        {loading ? (
          <TicketChatSkeleton />
        ) : (
          <>
            {descriptionMessage ? renderMessage(descriptionMessage) : null}
            {descriptionMessage && hasChatMessages ? (
              <Divider sx={chatDividerSx} />
            ) : null}
            {hasChatMessages && showPaginationChip ? (
              <Box display="flex" justifyContent="center" my={0.5}>
                <Chip
                  label={loadMoreLabel}
                  onClick={canLoadMore ? () => loadPage(page + 1, { append: true, preserveScroll: true }) : undefined}
                  disabled={loadingMore || !canLoadMore}
                  variant="outlined"
                  size="small"
                  sx={chatPaginationChipSx}
                />
              </Box>
            ) : null}
            {visibleMessages.map((msg) => renderMessage(msg))}
          </>
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", fontSize: "0.875rem" }}>
            {isEditing ? "Modifica messaggio" : "Messaggio"}
          </Typography>
          {isEditing ? (
            <Typography
              component="button"
              type="button"
              onClick={resetComposer}
              sx={{
                fontFamily: "Rubik",
                fontSize: "0.75rem",
                color: "#808080",
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                textDecoration: "underline",
              }}
            >
              Annulla
            </Typography>
          ) : null}
        </Box>
        <Typography sx={{ fontFamily: "Rubik", color: "#808080", fontSize: "0.875rem" }}>
          {messageText.length}/{MAX_MESSAGE_LEN}
        </Typography>
      </Box>
      <Box display="flex" alignItems="flex-end" gap={1} mb={1}>
        {!chatEnabled ? (
          <Tooltip title={CHAT_DISABLED_TOOLTIP} placement="top" arrow>
            <Box sx={{ flex: 1, opacity: 0.75 }}>{messageField}</Box>
          </Tooltip>
        ) : (
          messageField
        )}
        <Box mt={1} display="flex" alignItems="center" gap={1}>
          <Tooltip title={showAttachmentRemove ? "Rimuovi allegato" : "Allegato"} placement="top">
            <span>
              {showAttachmentRemove ? (
                <IconButton
                  disabled={!chatEnabled || sending}
                  onClick={handleRemoveAttachment}
                  sx={roundActionIconSx}
                >
                  <LinkOff sx={{ color: "#e00036", transform: "rotate(90deg)" }} />
             
                </IconButton>
              ) : (
                <IconButton
                  component="label"
                  disabled={!chatEnabled || sending}
                  sx={roundActionIconSx}
                >
                  <AttachFileOutlined />
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={handleFileChange}
                  />
                </IconButton>
              )}
            </span>
          </Tooltip>
          <Tooltip title={isEditing ? "Salva modifiche" : "Invia"} placement="top">
            <span>
              <IconButton
                disabled={!chatEnabled || sending || !canSubmit}
                onClick={handleSend}
                sx={roundActionIconSx}
              >
                {sending ? <CircularProgress size={20} /> : <SendOutlined />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {pendingAttachmentLabel ? (
        <Typography variant="caption" sx={{ display: "block", fontFamily: "Rubik", color: "#808080" }}>
          Allegato: {pendingAttachmentLabel}
        </Typography>
      ) : null}
      {attachmentError ? (
        <Typography variant="caption" color="error.main" sx={{ display: "block", fontFamily: "Rubik" }}>
          {attachmentError}
        </Typography>
      ) : null}
    </Box>
  );
};

TicketChat.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    createdAt: PropTypes.any,
    description: PropTypes.string,
  }).isRequired,
  disabled: PropTypes.bool,
  onMessagesRead: PropTypes.func,
};

TicketChat.defaultProps = {
  disabled: false,
  onMessagesRead: null,
};

export default TicketChat;
